import { useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { Slide } from '@components/animators/slide';
import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState, EmptyStateCell } from '@components/empty-state';
import { useCreateFormStore } from '@components/form';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconHelpSimple } from '@components/icons/help-simple';
import { IconList } from '@components/icons/list';
import { IconMarketplace } from '@components/icons/marketplace';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconReplace } from '@components/icons/replace';
import { IconSearch } from '@components/icons/search';
import { IntegrationIcon } from '@components/integrations/common';
import { RelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { SearchBox } from '@components/search-box';
import { Sidebar, forceHideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';

import {
  useMarketplaceIntegrationLists,
  useMarketplaceIntegrationsConnected,
} from 'api/app-marketplace/hooks';
import { MarketplaceIntegrationWithLists, MarketplaceList } from 'api/app-marketplace/requests';
import { TravellerAttributeTemplateData } from 'api/attributes/requests';
import { useLists } from 'api/lists/hooks';
import { useFeature } from 'ui/hooks';

import {
  DetailsSection,
  FieldFormSection,
  FieldFormWithUnsavedChangesPrompt,
  FieldFormData,
  Form,
  FormFooter,
  applyInputStatus,
  locale,
} from './field-form-shared';

// =============================================================================
// Multi-step flow controller
// =============================================================================

type ListSourceType = 'global' | 'marketplace';

type SelectFieldStep =
  | 'listSource'
  | 'listSelection'
  | 'integrationSelection'
  | 'marketplaceListSelection'
  | 'form';

export type SelectFieldFlowRef = {
  goBack: () => boolean;
};

type SelectFieldFlowProps = {
  attributeType: 'single_select' | 'multi_select';
  attribute?: TravellerAttributeTemplateData;
  isTravellerAttribute: boolean;
  onSubmit: (data: FieldFormData) => Promise<boolean>;
  isEditing: boolean;
  flowRef?: React.RefObject<SelectFieldFlowRef | null>;
  setCanGoBack?: (canGoBack: boolean) => void;
};

export const SelectFieldFlow = ({
  attributeType,
  attribute,
  isTravellerAttribute,
  onSubmit,
  isEditing,
  flowRef,
  setCanGoBack,
}: SelectFieldFlowProps) => {
  const { t } = useScopedTranslate(locale);
  // Include hidden lists so we can display them when editing fields created in form builder
  const listsQuery = useLists('?include_hidden=1');
  const marketplaceListsQuery = useMarketplaceIntegrationLists();
  const connectedIntegrationsQuery = useMarketplaceIntegrationsConnected();
  const { isEnabled: hasMarketplaceFeature } = useFeature('FEATURE_MARKETPLACE');

  const allConnectedIntegrations = useMemo((): MarketplaceIntegrationWithLists[] => {
    const listsData = marketplaceListsQuery.data ?? [];
    const connectedData = connectedIntegrationsQuery.data ?? [];

    const listsMap = new Map(listsData.map((integration) => [integration.shortId, integration]));

    // Add connected integrations that aren't in the lists data (they have 0 lists)
    const result = [...listsData];
    for (const connected of connectedData) {
      if (!listsMap.has(connected.shortId)) {
        result.push({
          shortId: connected.shortId,
          name: connected.name,
          logo: connected.logo,
          lists: [],
        });
      }
    }

    return result.sort((a, b) => a.name.localeCompare(b.name));
  }, [marketplaceListsQuery.data, connectedIntegrationsQuery.data]);

  const initialStep: SelectFieldStep = isEditing ? 'form' : 'listSource';
  const [step, setStep] = useState<SelectFieldStep>(initialStep);
  const [listSourceType, setListSourceType] = useState<ListSourceType | undefined>(undefined);
  const [selectedListId, setSelectedListId] = useState<string | undefined>(
    attribute?.options ?? undefined
  );
  const [selectedIntegration, setSelectedIntegration] = useState<
    MarketplaceIntegrationWithLists | undefined
  >(undefined);
  const [selectedMarketplaceList, setSelectedMarketplaceList] = useState<
    MarketplaceList | undefined
  >(undefined);

  const selectedList = useMemo(() => {
    if (!selectedListId || !listsQuery.data) {
      return undefined;
    }
    return listsQuery.data.find((list) => {
      return list.shortId === selectedListId;
    });
  }, [selectedListId, listsQuery.data]);

  const existingMarketplaceSelection = useMemo(() => {
    if (!attribute?.options || !marketplaceListsQuery.data) {
      return undefined;
    }
    for (const integration of marketplaceListsQuery.data) {
      const list = integration.lists.find((list) => list.shortId === attribute.options);
      if (list) {
        return { integration, list };
      }
    }
    return undefined;
  }, [attribute?.options, marketplaceListsQuery.data]);

  useEffect(() => {
    if (isEditing && existingMarketplaceSelection && !selectedIntegration) {
      setSelectedIntegration(existingMarketplaceSelection.integration);
      setSelectedMarketplaceList(existingMarketplaceSelection.list);
      setListSourceType('marketplace');
    }
  }, [isEditing, existingMarketplaceSelection, selectedIntegration]);

  const handleSelectGlobalList = (list: ListItem) => {
    setSelectedListId(list.shortId);
    setListSourceType('global');
    setStep('form');
  };

  const handleSelectIntegration = (integration: MarketplaceIntegrationWithLists) => {
    setSelectedIntegration(integration);
    setStep('marketplaceListSelection');
  };

  const handleSelectMarketplaceList = (list: ListItem) => {
    const marketplaceList = list as MarketplaceList;
    setSelectedMarketplaceList(marketplaceList);
    setSelectedListId(marketplaceList.shortId);
    setListSourceType('marketplace');
    setStep('form');
  };

  const handleChangeList = () => {
    if (listSourceType === 'marketplace' && selectedIntegration) {
      setStep('marketplaceListSelection');
    } else if (listSourceType === 'marketplace') {
      setStep('integrationSelection');
    } else {
      setStep('listSelection');
    }
  };

  useImperativeHandle(flowRef, () => ({
    goBack: () => {
      if (step === 'listSource') {
        if (isEditing) {
          setStep('form');
          return true;
        }
        return false;
      }
      if (step === 'listSelection' || step === 'integrationSelection') {
        setStep('listSource');
        return true;
      }
      if (step === 'marketplaceListSelection') {
        setStep('integrationSelection');
        return true;
      }
      if (step === 'form') {
        if (listSourceType === 'marketplace') {
          setStep('marketplaceListSelection');
        } else {
          setStep('listSelection');
        }
        return true;
      }
      return false;
    },
  }));

  useEffect(() => {
    setCanGoBack?.(step !== initialStep);
  }, [step, initialStep, setCanGoBack]);

  if (step === 'listSource') {
    return (
      <ListSourceSelection
        onSelectGlobalLists={() => {
          setListSourceType('global');
          setStep('listSelection');
        }}
        onSelectMarketplace={
          hasMarketplaceFeature
            ? () => {
                setListSourceType('marketplace');
                setStep('integrationSelection');
              }
            : undefined
        }
      />
    );
  }

  if (step === 'listSelection') {
    return (
      <ListSelection
        lists={listsQuery.data ?? []}
        isLoading={listsQuery.isLoading}
        onSelectList={handleSelectGlobalList}
      />
    );
  }

  if (step === 'integrationSelection') {
    return (
      <IntegrationSelection
        integrations={allConnectedIntegrations}
        isLoading={marketplaceListsQuery.isLoading || connectedIntegrationsQuery.isLoading}
        onSelectIntegration={handleSelectIntegration}
      />
    );
  }

  if (step === 'marketplaceListSelection' && selectedIntegration) {
    return (
      <ListSelection
        lists={selectedIntegration.lists}
        onSelectList={handleSelectMarketplaceList}
        emptyStateContent={t('noIntegrationListsDescription')}
      />
    );
  }

  if (listSourceType === 'marketplace' && selectedIntegration && selectedMarketplaceList) {
    return (
      <SelectFieldForm
        attributeType={attributeType}
        attribute={attribute}
        selectedList={{
          shortId: selectedMarketplaceList.shortId,
          name: selectedMarketplaceList.name,
        }}
        isTravellerAttribute={isTravellerAttribute}
        onSubmit={onSubmit}
        isEditing={isEditing}
        onChangeList={handleChangeList}
        marketplaceIntegration={selectedIntegration}
      />
    );
  }

  if (listsQuery.isLoading) {
    return <RelativeSpinner />;
  }

  return (
    <SelectFieldForm
      attributeType={attributeType}
      attribute={attribute}
      selectedList={selectedList}
      isTravellerAttribute={isTravellerAttribute}
      onSubmit={onSubmit}
      isEditing={isEditing}
      onChangeList={handleChangeList}
    />
  );
};

// =============================================================================
// Step 1 — Pick list source
// =============================================================================

type ListSourceSelectionProps = {
  onSelectGlobalLists: () => void;
  onSelectMarketplace?: () => void;
};

const ListSourceSelection = ({
  onSelectGlobalLists,
  onSelectMarketplace,
}: ListSourceSelectionProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Slide key="list-source-selection" offset={30}>
      <Stack space={6}>
        <FieldFormSection label={t('listType')}>
          <Stack space={2}>
            <Card space={3} variant="transparent" interactive onClick={onSelectGlobalLists}>
              <Stack orientation="horizontal" align="center" space={3}>
                <IconFancyContainer icon={<IconCheckmark />} variant="blue" size="md" />
                <Text text={t('selectFromGlobalLists')} font="semibold12" />
              </Stack>
            </Card>
            {onSelectMarketplace ? (
              <Card space={3} variant="transparent" interactive onClick={onSelectMarketplace}>
                <Stack orientation="horizontal" align="center" space={3}>
                  <IconFancyContainer icon={<IconMarketplace />} variant="indigo" size="md" />
                  <Text text={t('selectFromMarketplace')} font="semibold12" />
                </Stack>
              </Card>
            ) : (
              <Card space={3} variant="default" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                <Stack orientation="horizontal" align="center" space={3}>
                  <IconFancyContainer icon={<IconMarketplace />} variant="gray" size="md" />
                  <Text
                    text={t('selectFromMarketplaceComingSoon')}
                    font="semibold12"
                    color="textSecondary"
                  />
                </Stack>
              </Card>
            )}
          </Stack>
        </FieldFormSection>
      </Stack>
      <Sidebar.Footer>
        <Form.Actions>
          <Button text={t('core.cancel')} variant="secondary" onClick={forceHideSidebar} />
          <Button text={t('createField')} variant="primary-blue" isDisabled />
        </Form.Actions>
      </Sidebar.Footer>
    </Slide>
  );
};

// =============================================================================
// Step 2 — Pick a list (global or marketplace)
// =============================================================================

type ListItem = { shortId: string; name: string; isHidden?: boolean };

type ListSelectionProps<T extends ListItem> = {
  lists: T[];
  isLoading?: boolean;
  onSelectList: (list: T) => void;
  emptyStateContent?: string;
};

const ListSelection = <T extends ListItem>({
  lists,
  isLoading,
  onSelectList,
  emptyStateContent,
}: ListSelectionProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const [searchStr, setSearchStr] = useState('');

  // Filter out hidden lists (those are managed by form builder, not selectable here)
  const selectableLists = useMemo(() => {
    return lists.filter((list) => {
      return !list.isHidden;
    });
  }, [lists]);

  const filteredLists = useMemo(() => {
    if (!searchStr) {
      return selectableLists;
    }
    const search = searchStr.toLowerCase();
    return selectableLists.filter((list) => {
      return list.name.toLowerCase().includes(search);
    });
  }, [selectableLists, searchStr]);

  const hasNoLists = selectableLists.length === 0;
  const hasNoResults = searchStr && filteredLists.length === 0;

  if (isLoading) {
    return <RelativeSpinner />;
  }

  const renderContent = () => {
    if (hasNoLists) {
      return (
        <EmptyState
          variant="small"
          icon={<IconList />}
          header={t('noLists')}
          content={emptyStateContent ?? t('noListsDescription')}
          skipMargin
        />
      );
    }

    return (
      <Stack space={4}>
        <SearchBox placeholder={t('searchForList')} value={searchStr} onChange={setSearchStr} />
        {hasNoResults ? (
          <EmptyState
            variant="small"
            icon={<IconSearch />}
            header={t('noResultsFound')}
            content={t('noResultsFoundDescription')}
            skipMargin
          />
        ) : (
          <Stack space={2}>
            {filteredLists.map((list) => {
              return (
                <Card key={list.shortId} spaceX={4} spaceY={3} variant="default">
                  <Stack orientation="horizontal" align="center" justify="space-between" space={2}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text text={list.name} font="semibold14" ellipsis />
                    </div>
                    <Button
                      text={t('select')}
                      variant="primary-blue"
                      size="sm"
                      onClick={() => {
                        return onSelectList(list);
                      }}
                    />
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <Slide key="list-selection" offset={30}>
      <Stack space={6}>
        <FieldFormSection label={t('selectList')}>{renderContent()}</FieldFormSection>
      </Stack>
    </Slide>
  );
};

// =============================================================================
// Step 2b — Pick a marketplace integration
// =============================================================================

type IntegrationSelectionProps = {
  integrations: MarketplaceIntegrationWithLists[];
  isLoading: boolean;
  onSelectIntegration: (integration: MarketplaceIntegrationWithLists) => void;
};

const IntegrationSelection = ({
  integrations,
  isLoading,
  onSelectIntegration,
}: IntegrationSelectionProps) => {
  const { t } = useScopedTranslate(locale);
  const [searchStr, setSearchStr] = useState('');

  const filteredIntegrations = useMemo(() => {
    if (!searchStr) {
      return integrations;
    }
    const search = searchStr.toLowerCase();
    return integrations.filter((integration) => {
      return integration.name.toLowerCase().includes(search);
    });
  }, [integrations, searchStr]);

  if (isLoading) {
    return <RelativeSpinner />;
  }

  if (integrations.length === 0) {
    return (
      <Slide key="integration-selection" offset={30}>
        <Stack space={6}>
          <FieldFormSection label={t('selectIntegration')}>
            <EmptyStateCell icon={<IconMarketplace />} text={t('noConnectedApps')} />
          </FieldFormSection>
        </Stack>
      </Slide>
    );
  }

  const hasNoSearchResults = searchStr && filteredIntegrations.length === 0;

  return (
    <Slide key="integration-selection" offset={30}>
      <Stack space={6}>
        <FieldFormSection label={t('selectIntegration')}>
          <Stack space={4}>
            <SearchBox
              placeholder={t('searchForIntegration')}
              value={searchStr}
              onChange={setSearchStr}
            />
            {hasNoSearchResults ? (
              <EmptySearchState variant="small" skipMargin />
            ) : (
              <Stack space={2}>
                {filteredIntegrations.map((integration) => {
                  return (
                    <Card
                      key={integration.shortId}
                      spaceX={4}
                      spaceY={3}
                      variant="transparent"
                      interactive
                      onClick={() => {
                        return onSelectIntegration(integration);
                      }}
                    >
                      <Stack
                        orientation="horizontal"
                        align="center"
                        justify="space-between"
                        space={2}
                      >
                        <Stack
                          orientation="horizontal"
                          align="center"
                          space={4}
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          <IntegrationIcon src={integration.logo} size="md" />
                          <Text text={integration.name} font="semibold14" ellipsis />
                        </Stack>
                        <Badge text={integration.lists.length} variant="neutral" />
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </FieldFormSection>
      </Stack>
    </Slide>
  );
};

// =============================================================================
// Step 3 — Configure the field
// =============================================================================

type SelectedListSummary = {
  shortId: string;
  name: string;
  isHidden?: boolean;
};

type SelectFieldFormProps = {
  attributeType: 'single_select' | 'multi_select';
  attribute?: TravellerAttributeTemplateData;
  selectedList?: SelectedListSummary;
  isTravellerAttribute: boolean;
  onSubmit: (data: FieldFormData) => Promise<boolean>;
  isEditing: boolean;
  onChangeList: () => void;
  marketplaceIntegration?: MarketplaceIntegrationWithLists;
};

const SelectFieldForm = ({
  attributeType,
  attribute,
  selectedList,
  isTravellerAttribute,
  onSubmit,
  isEditing,
  onChangeList,
  marketplaceIntegration,
}: SelectFieldFormProps) => {
  const formStore = useCreateFormStore();
  const isFormDirty = formStore.useIsDirty();
  const isListMissing = !selectedList;
  const isListChanged = !isListMissing && selectedList.shortId !== attribute?.options;
  const isDirty = isFormDirty || isListChanged;

  const handleSubmit = async (data: FieldFormData) => {
    const submitData = applyInputStatus(
      { ...data, attributeType, options: selectedList?.shortId ?? '' },
      isTravellerAttribute
    );
    return onSubmit(submitData);
  };

  return (
    <Slide key="select-field-form" offset={30}>
      <FieldFormWithUnsavedChangesPrompt
        onSubmit={handleSubmit}
        store={formStore}
        overwriteIsDirty={isDirty}
      >
        <Stack space={6}>
          <DetailsSection
            attributeType={attributeType}
            attribute={attribute}
            isTravellerAttribute={isTravellerAttribute}
          >
            <SelectedListCard
              list={selectedList}
              integration={marketplaceIntegration}
              onChangeList={onChangeList}
            />
          </DetailsSection>
        </Stack>
        <FormFooter isEditing={isEditing} isDirty={isDirty} isDisabled={isListMissing} />
      </FieldFormWithUnsavedChangesPrompt>
    </Slide>
  );
};

// =============================================================================
// Card showing the currently-selected list inside the configuration step
// =============================================================================

type SelectedListCardProps = {
  list?: SelectedListSummary;
  integration?: MarketplaceIntegrationWithLists;
  onChangeList?: () => void;
};

const SelectedListCard = ({ list, integration, onChangeList }: SelectedListCardProps) => {
  const { t } = useScopedTranslate(locale);

  const hasList = Boolean(list);
  const isHidden = list?.isHidden ?? false;
  const displayName = !list
    ? t('listNotFound')
    : isHidden
    ? t('listInsideExternalForm')
    : list.name;
  const textColor: Color | undefined = hasList ? undefined : 'red100';
  const showChangeOption = onChangeList && !isHidden;

  return (
    <Card space={3} variant="default">
      <Stack orientation="horizontal" align="center" justify="space-between" space={2}>
        <Stack orientation="horizontal" align="center" space={4} style={{ flex: 1, minWidth: 0 }}>
          {!hasList ? (
            <IconHelpSimple color="red100" />
          ) : integration ? (
            <IntegrationIcon src={integration.logo} size="sm" />
          ) : (
            <IconList color="elementPurple80" />
          )}
          <div style={{ minWidth: 0 }}>
            <Text text={displayName} font="semibold14" color={textColor} ellipsis={hasList} />
            {integration && (
              <Text text={integration.name} font="regular12" color="textSecondary" ellipsis />
            )}
          </div>
        </Stack>
        {showChangeOption && (
          <Menu
            buttonProps={{
              variant: 'ghost',
              iconRight: <IconMoreVertical />,
            }}
          >
            <Menu.Item label={t('replaceList')} icon={<IconReplace />} onSelect={onChangeList} />
          </Menu>
        )}
      </Stack>
    </Card>
  );
};
